<template>
  <main class="main page-inside sell-page">
    <section class="section sell-section">
      <div class="wrapper">
        <!-- PAGE HEADER -->
        <div class="sell-header">
          <h1 class="title big"><b>SELL SKINS</b></h1>

          <div class="sell-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              class="sell-tab"
              :class="{ active: activeTab === tab.id }"
              @click="selectTab(tab.id)"
            >
              {{ tab.title }}
            </button>
          </div>
        </div>

        <!-- MESSAGES -->
        <div v-if="error" class="sell-message error">
          {{ error }}
        </div>

        <div v-if="notice" class="sell-message success">
          {{ notice }}
        </div>

        <!-- LOGIN -->
        <div v-if="!isAuth" class="sell-panel sell-empty">
          <h2 class="title medium"><b>STEAM LOGIN REQUIRED</b></h2>
          <button type="button" class="button" @click="openLogin">
            SIGN IN
          </button>
        </div>

        <template v-else>
          <!-- INVENTORY -->
          <section v-if="activeTab === 'inventory'" class="sell-panel">
            <div class="sell-panel-header">
              <h2 class="title medium"><b>YOUR INVENTORY</b></h2>

              <button
                type="button"
                class="button black sell-small-button"
                :disabled="loading || busy"
                @click="loadInventory"
              >
                REFRESH
              </button>
            </div>

            <div v-if="loading" class="sell-empty">LOADING...</div>

            <div v-else-if="!inventory.length" class="sell-empty">
              NO ITEMS AVAILABLE
            </div>

            <div v-else class="sell-grid">
              <article
                v-for="item in inventory"
                :key="item.short_name"
                class="sell-item"
                :class="{
                  selected: item.qty > 0,
                  disabled: !canSellItem(item),
                }"
              >
                <div class="sell-item-top">
                  <span class="sell-category">
                    {{ item.category || 'RUST ITEM' }}
                  </span>

                  <span class="sell-owned"> ×{{ item.amount }} </span>
                </div>

                <h3 class="sell-item-name">
                  {{ item.game_name || item.short_name }}
                </h3>

                <div class="sell-item-footer">
                  <strong v-if="canSellItem(item)" class="sell-price">
                    {{ money(item.price) }} {{ currency }}
                  </strong>

                  <span v-else class="sell-not-available"> UNAVAILABLE </span>

                  <div v-if="canSellItem(item)" class="sell-quantity">
                    <button
                      type="button"
                      :disabled="busy || item.qty <= 0"
                      @click="changeQty(item, -1)"
                    >
                      −
                    </button>

                    <span>{{ item.qty }}</span>

                    <button
                      type="button"
                      :disabled="busy || item.qty >= item.amount"
                      @click="changeQty(item, 1)"
                    >
                      +
                    </button>
                  </div>
                </div>
              </article>
            </div>

            <div class="sell-summary">
              <div class="sell-summary-total">
                <span>{{ selectedCount }} ITEMS SELECTED</span>

                <strong> {{ money(selectedTotal) }} {{ currency }} </strong>
              </div>

              <button
                type="button"
                class="button sell-submit"
                :disabled="loading || busy || !canSubmit"
                @click="submitRequest"
              >
                {{ busy ? 'PLEASE WAIT...' : 'SELL ITEMS' }}
              </button>
            </div>
          </section>

          <!-- OFFERS -->
          <section v-if="activeTab === 'offers'" class="sell-panel">
            <div class="sell-panel-header">
              <h2 class="title medium"><b>MY OFFERS</b></h2>

              <button
                type="button"
                class="button black sell-small-button"
                :disabled="loading || busy"
                @click="loadOffers()"
              >
                REFRESH
              </button>
            </div>

            <div v-if="loading" class="sell-empty">LOADING...</div>

            <div v-else-if="!offers.length" class="sell-empty">
              NO OFFERS YET
            </div>

            <article v-for="offer in offers" :key="offer.id" class="sell-offer">
              <div class="sell-offer-header">
                <h3 class="title small">
                  <b>OFFER #{{ offer.id }}</b>
                </h3>

                <span class="sell-status" :class="statusClass(offer.status)">
                  {{ statusLabel(offer.status) }}
                </span>
              </div>

              <div class="sell-offer-values">
                <div>
                  <span>REQUESTED</span>

                  <strong>
                    {{ money(offer.total_amount) }}
                    {{ offer.currency || currency }}
                  </strong>
                </div>

                <div v-if="offer.counter_offer_amount != null">
                  <span>ADMIN OFFER</span>

                  <strong class="yellow">
                    {{ money(offer.counter_offer_amount) }}
                    {{ offer.currency || currency }}
                  </strong>
                </div>
              </div>

              <div
                v-if="offer.items && offer.items.length"
                class="sell-offer-items"
              >
                <span v-for="(item, index) in offer.items" :key="index">
                  {{ item.short_name }} ×{{ item.quantity }}
                </span>
              </div>

              <p v-if="offer.admin_note" class="sell-note">
                {{ offer.admin_note }}
              </p>

              <div
                v-if="offer.status === 'CounterOffer'"
                class="sell-offer-actions"
              >
                <button
                  type="button"
                  class="button"
                  :disabled="busy || loading"
                  @click="replyToOffer(offer, 'approve')"
                >
                  ACCEPT
                </button>

                <button
                  type="button"
                  class="button black"
                  :disabled="busy || loading"
                  @click="replyToOffer(offer, 'decline')"
                >
                  DECLINE
                </button>
              </div>
            </article>

            <button
              v-if="offersPage < lastOffersPage"
              type="button"
              class="button black sell-more"
              :disabled="loading || busy"
              @click="loadOffers(offersPage + 1)"
            >
              LOAD MORE
            </button>
          </section>

          <!-- WITHDRAW -->
          <section v-if="activeTab === 'withdraw'" class="sell-panel">
            <div class="sell-panel-header">
              <h2 class="title medium"><b>WITHDRAW</b></h2>

              <button
                type="button"
                class="button black sell-small-button"
                :disabled="loading || busy"
                @click="loadWithdrawals"
              >
                REFRESH
              </button>
            </div>

            <div class="sell-wallet">
              <span>SELLER BALANCE</span>

              <strong>
                {{
                  walletBalance === null
                    ? 'UNAVAILABLE'
                    : money(walletBalance) + ' ' + currency
                }}
              </strong>
            </div>

            <div class="sell-withdraw-form">
              <input
                v-model="withdrawAmount"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="Amount"
              />

              <button
                type="button"
                class="button"
                :disabled="
                  busy ||
                  loading ||
                  walletBalance === null ||
                  walletBalance <= 0
                "
                @click="requestWithdrawal"
              >
                REQUEST WITHDRAWAL
              </button>
            </div>

            <h2 class="title medium sell-history-title">
              <b>HISTORY</b>
            </h2>

            <div v-if="loading" class="sell-empty">LOADING...</div>

            <div v-else-if="!withdrawals.length" class="sell-empty">
              NO WITHDRAWALS YET
            </div>

            <article
              v-for="request in withdrawals"
              :key="request.id"
              class="sell-offer"
            >
              <div class="sell-offer-header">
                <h3 class="title small">
                  <b>WITHDRAWAL #{{ request.id }}</b>
                </h3>

                <span class="sell-status" :class="statusClass(request.status)">
                  {{ statusLabel(request.status) }}
                </span>
              </div>

              <strong class="sell-withdraw-amount">
                {{ money(request.amount) }}
                {{ request.currency || currency }}
              </strong>
            </article>
          </section>
        </template>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: 'SellSkins',

  props: {
    isAuth: {
      type: Boolean,
      default: false,
    },
    currencyCode: {
      type: String,
      default: 'EUR',
    },
    profileData: {
      type: Object,
      default: () => ({}),
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      tabs: [
        { id: 'inventory', title: 'SELL ITEMS' },
        { id: 'offers', title: 'MY OFFERS' },
        { id: 'withdraw', title: 'WITHDRAW' },
      ],

      activeTab: 'inventory',
      inventory: [],
      offers: [],
      withdrawals: [],

      offersPage: 1,
      lastOffersPage: 1,

      walletBalance: null,
      withdrawAmount: '',

      loading: false,
      busy: false,
      error: '',
      notice: '',
    }
  },

  computed: {
    currency() {
      return this.currencyCode || 'EUR'
    },

    selectedItems() {
      return this.inventory.filter(
        (item) =>
          this.canSellItem(item) &&
          Number.isInteger(item.qty) &&
          item.qty > 0 &&
          item.qty <= item.amount,
      )
    },

    canSubmit() {
      return this.selectedItems.length > 0
    },

    selectedCount() {
      return this.selectedItems.reduce((total, item) => total + item.qty, 0)
    },

    selectedTotal() {
      return this.selectedItems.reduce(
        (total, item) => total + item.qty * item.price,
        0,
      )
    },
  },

  watch: {
    isAuth(authenticated) {
      if (authenticated) {
        this.loadInventory()
      } else {
        this.inventory = []
        this.offers = []
        this.withdrawals = []
        this.walletBalance = null
      }
    },
  },

  mounted() {
    if (this.isAuth) {
      this.loadInventory()
    }
  },

  methods: {
    money(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : '0.00'
    },

    openLogin() {
      if (this.$parent && this.$parent.openSignInModal) {
        this.$parent.openSignInModal()
      } else {
        this.$emit('openSignInModal')
      }
    },

    canSellItem(item) {
      return Boolean(
        item.in_catalog &&
        Number.isFinite(item.price) &&
        item.price > 0 &&
        item.amount > 0,
      )
    },

    fail(error) {
      return (
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        'Request failed. Please try again.'
      )
    },

    clearMessages() {
      this.error = ''
      this.notice = ''
    },

    statusLabel(status) {
      const labels = {
        Pending: 'PENDING',
        Approved: 'APPROVED',
        Rejected: 'REJECTED',
        Completed: 'COMPLETED',
        Cancelled: 'CANCELLED',
        CounterOffer: 'NEW OFFER',
        UserDeclined: 'DECLINED',
        Processing: 'PROCESSING',
        Failed: 'FAILED',
      }

      return labels[status] || String(status || 'UNKNOWN').toUpperCase()
    },

    statusClass(status) {
      if (status === 'Completed') return 'completed'
      if (status === 'CounterOffer') return 'counter'
      if (
        status === 'Rejected' ||
        status === 'UserDeclined' ||
        status === 'Failed'
      )
        return 'rejected'

      return ''
    },

    changeQty(item, change) {
      if (this.busy || !this.canSellItem(item)) return

      const current = Number(item.qty) || 0

      item.qty = Math.max(0, Math.min(item.amount, current + change))
    },

    async selectTab(tab) {
      this.activeTab = tab
      this.clearMessages()

      if (!this.isAuth) return

      if (tab === 'inventory') await this.loadInventory()
      if (tab === 'offers') await this.loadOffers()
      if (tab === 'withdraw') await this.loadWithdrawals()
    },

    async loadInventory() {
      this.loading = true
      this.error = ''

      try {
        const response = await this.$http.get('user/inventory')

        if (response.data.status !== 'OK') {
          throw new Error('Inventory unavailable')
        }

        const payload = response.data.payload || []

        // Backend sells by short_name, not by skin or slot.
        // Merge matching item types to avoid duplicate rows.
        const grouped = {}

        if (Array.isArray(payload)) {
          payload.forEach((item) => {
            const key = item.short_name
            const amount = Number(item.amount) || 0

            if (!key || amount <= 0) return

            const catalog = item.catalog_item || null
            const price = catalog ? Number(catalog.price) : 0
            const eligible = Boolean(
              item.in_catalog && catalog && Number.isFinite(price) && price > 0,
            )

            if (!grouped[key]) {
              grouped[key] = {
                short_name: key,
                game_name: item.game_name || key,
                amount: 0,
                price: eligible ? price : 0,
                category: catalog ? catalog.category : '',
                in_catalog: eligible,
                qty: 0,
              }
            }

            grouped[key].amount += amount
          })
        }

        this.inventory = Object.values(grouped)
      } catch (error) {
        this.error = this.fail(error)
        this.inventory = []
      } finally {
        this.loading = false
      }
    },

    async submitRequest() {
      if (!this.canSubmit || this.busy || this.loading) return

      this.busy = true
      this.clearMessages()

      const items = this.selectedItems.map((item) => ({
        short_name: item.short_name,
        quantity: Number(item.qty),
        price: Number(item.price),
      }))

      try {
        const response = await this.$http.post('user/sell-requests', { items })

        if (response.data.status !== 'OK') {
          this.error = response.data.message || 'Failed to submit sell request.'
          return
        }

        this.notice = 'Sell request submitted successfully.'

        // Reset selected quantities
        this.inventory.forEach((item) => {
          item.qty = 0
        })

        // Switch to offers
        this.activeTab = 'offers'

        try {
          await this.loadOffers()
        } catch (refreshError) {
          // The sell request was already submitted.
        }

        this.notice = 'Sell request submitted successfully.'
      } catch (error) {
        this.error = this.fail(error)
      } finally {
        this.busy = false
      }
    },

    async loadOffers(page = 1) {
      this.loading = true
      this.error = ''

      try {
        const response = await this.$http.get('user/sell-requests', {
          params: { page },
        })

        const payload = response.data.payload || []

        const rows = Array.isArray(payload) ? payload : payload.data || []

        this.offers = page === 1 ? rows : this.offers.concat(rows)

        this.offersPage = page

        this.lastOffersPage =
          (response.data.meta && response.data.meta.last_page) || 1
      } catch (error) {
        this.error = this.fail(error)
      } finally {
        this.loading = false
      }
    },

    async replyToOffer(offer, action) {
      if (this.busy || this.loading || offer.status !== 'CounterOffer') return

      if (action !== 'approve' && action !== 'decline') return

      const accepting = action === 'approve'

      this.busy = true
      this.clearMessages()

      try {
        const response = await this.$http.post(
          'user/sell-requests/' + offer.id + '/' + action,
          {},
        )

        if (response.data.status !== 'OK') {
          throw new Error(response.data.message || 'Offer action failed')
        }

        await this.loadOffers()

        this.notice = accepting
          ? 'Offer accepted successfully.'
          : 'Offer declined successfully.'
      } catch (error) {
        this.error = this.fail(error)
      } finally {
        this.busy = false
      }
    },

    async loadWithdrawals() {
      this.loading = true
      this.error = ''
      this.walletBalance = null

      try {
        const profileResponse = await this.$http.get('user/profile')

        const profile = profileResponse.data.payload || {}

        // Seller payout balance (NOT the normal shop balance)
        if (
          profile.payout_balance !== null &&
          profile.payout_balance !== undefined
        ) {
          const balance = Number(profile.payout_balance)

          this.walletBalance = Number.isFinite(balance) ? balance : null
        }

        const response = await this.$http.get('user/withdraw-requests')

        const payload = response.data.payload || []

        this.withdrawals = Array.isArray(payload) ? payload : payload.data || []
      } catch (error) {
        this.error = this.fail(error)
      } finally {
        this.loading = false
      }
    },

    async requestWithdrawal() {
      if (this.busy || this.loading) return

      const rawAmount = String(this.withdrawAmount).trim()
      const amount = Number(rawAmount)

      this.clearMessages()

      if (
        !/^\d+(\.\d{1,2})?$/.test(rawAmount) ||
        !Number.isFinite(amount) ||
        amount <= 0
      ) {
        this.error = 'Enter a valid withdrawal amount.'
        return
      }

      if (this.walletBalance === null || amount > this.walletBalance) {
        this.error = 'Insufficient seller balance.'
        return
      }

      this.busy = true

      try {
        const response = await this.$http.post('user/withdraw-requests', {
          amount,
        })

        if (response.data.status !== 'OK') {
          throw new Error(response.data.message || 'Withdrawal request failed')
        }

        this.withdrawAmount = ''

        await this.loadWithdrawals()

        this.notice = 'Withdrawal request submitted successfully.'
      } catch (error) {
        this.error = this.fail(error)
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
/* EXACT VAULTHEX COLOR FAMILY */

.sell-page {
  background: #0c0d10;
  min-height: 85vh;
  color: #fff;
}

.sell-section {
  position: relative;
}

.sell-section .wrapper {
  max-width: 1200px;
  position: relative;
  z-index: 1;
}

/* HEADER */

.sell-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 32px;
}

.sell-header h1 {
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 40px;
  line-height: 1.15;
  color: #fff;
}

/* TABS */

.sell-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.sell-tab {
  padding: 13px 19px;
  min-height: 46px;
  background: #25272e;
  border: 1px solid #343a4a;
  border-radius: 6px;
  color: #fff;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.sell-tab:hover {
  background: #343a4a;
}

.sell-tab.active {
  background: #eabd0a;
  color: #0c0d10;
  border-color: #eabd0a;
}

/* PANEL */

.sell-panel {
  padding: 26px;
  background: #1c1f26;
  border: 1px solid #343a4a;
  border-radius: 6px;
}

.sell-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 26px;
}

.sell-panel-header h2 {
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 24px;
  color: #fff;
}

.sell-small-button {
  min-width: 110px;
  padding: 12px 18px;
}

.sell-empty {
  padding: 35px 16px;
  text-align: center;
  color: #b0b4bc;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
}

.sell-empty h2 {
  margin-bottom: 22px;
}

/* INVENTORY */

.sell-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(205px, 1fr));
  gap: 14px;
}

.sell-item {
  display: flex;
  flex-direction: column;
  min-height: 174px;
  padding: 17px;
  background: #25272e;
  border: 1px solid #343a4a;
  border-radius: 6px;
  transition:
    border-color 0.2s,
    background 0.2s;
}

.sell-item:hover {
  border-color: #5d6473;
}

.sell-item.selected {
  border-color: #eabd0a;
  background: #292b2d;
}

.sell-item.disabled {
  opacity: 0.55;
}

.sell-item-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.sell-category {
  color: #b0b4bc;
  font-family: 'Roboto', sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.sell-owned {
  padding: 5px 9px;
  border-radius: 4px;
  background: #0c0d10;
  color: #fff;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.sell-item-name {
  margin: 0 0 18px;
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.sell-item-footer {
  margin-top: auto;
}

.sell-price {
  display: block;
  margin-bottom: 14px;
  color: #eabd0a;
  font-family: 'Roboto', sans-serif;
  font-size: 17px;
  font-weight: 700;
}

.sell-not-available {
  color: #969696;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.sell-quantity {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 42px;
  background: #0c0d10;
  border-radius: 5px;
  overflow: hidden;
}

.sell-quantity button {
  width: 44px;
  height: 42px;
  background: #343a4a;
  border: 0;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  cursor: pointer;
}

.sell-quantity button:hover:not(:disabled) {
  background: #eabd0a;
  color: #0c0d10;
}

.sell-quantity span {
  color: #fff;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 700;
}

/* SUMMARY */

.sell-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding-top: 25px;
  margin-top: 25px;
  border-top: 1px solid #343a4a;
}

.sell-summary-total {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.sell-summary-total span {
  color: #b0b4bc;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.sell-summary-total strong {
  color: #eabd0a;
  font-family: 'Montserrat', sans-serif;
  font-size: 27px;
  font-weight: 700;
}

.sell-submit {
  min-width: 200px;
}

/* BUTTONS */

.sell-page .button {
  background: #eabd0a;
  color: #0c0d10;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
}

.sell-page .button.black {
  background: #25272e;
  color: #fff;
}

.sell-page .button:hover:not(:disabled) {
  opacity: 0.75;
}

.sell-page button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* OFFERS */

.sell-offer {
  padding: 20px;
  margin-bottom: 14px;
  background: #25272e;
  border: 1px solid #343a4a;
  border-radius: 6px;
}

.sell-offer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.sell-offer-header h3 {
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  color: #fff;
}

.sell-status {
  padding: 7px 11px;
  background: rgba(234, 189, 10, 0.1);
  color: #eabd0a;
  border-radius: 4px;
  font-family: 'Roboto', sans-serif;
  font-size: 11px;
  font-weight: 700;
}

.sell-status.completed {
  background: rgba(52, 184, 13, 0.12);
  color: #34b80d;
}

.sell-status.rejected {
  background: rgba(235, 1, 27, 0.12);
  color: #f06b76;
}

.sell-status.counter {
  background: rgba(234, 189, 10, 0.18);
  color: #eabd0a;
}

.sell-offer-values {
  display: flex;
  gap: 35px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.sell-offer-values div {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sell-offer-values span {
  color: #b0b4bc;
  font-family: 'Roboto', sans-serif;
  font-size: 11px;
  font-weight: 700;
}

.sell-offer-values strong {
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  font-size: 19px;
}

.sell-offer-values strong.yellow {
  color: #eabd0a;
}

.sell-offer-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sell-offer-items span {
  padding: 7px 10px;
  background: #0c0d10;
  border-radius: 4px;
  color: #f4ece9;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
}

.sell-note {
  margin-top: 15px;
  color: #b0b4bc;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
}

.sell-offer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
}

.sell-more {
  margin-top: 12px;
}

/* WALLET */

.sell-wallet {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 25px;
  margin-bottom: 24px;
  background: #25272e;
  border: 1px solid #343a4a;
  border-radius: 6px;
}

.sell-wallet span {
  color: #b0b4bc;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.sell-wallet strong {
  color: #eabd0a;
  font-family: 'Montserrat', sans-serif;
  font-size: 32px;
  font-weight: 700;
}

.sell-withdraw-form {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 38px;
}

.sell-withdraw-form input {
  width: 230px;
  min-height: 48px;
  padding: 12px 15px;
  background: #0c0d10;
  border: 1px solid #343a4a;
  border-radius: 6px;
  color: #fff;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
}

.sell-withdraw-form input:focus {
  outline: none;
  border-color: #eabd0a;
}

.sell-history-title {
  margin-bottom: 22px;
  color: #fff;
}

.sell-withdraw-amount {
  color: #eabd0a;
  font-family: 'Montserrat', sans-serif;
  font-size: 20px;
}

/* MESSAGES */

.sell-message {
  padding: 15px 18px;
  margin-bottom: 20px;
  border-radius: 6px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
}

.sell-message.error {
  color: #ffb6bf;
  background: rgba(235, 1, 27, 0.13);
  border: 1px solid rgba(235, 1, 27, 0.4);
}

.sell-message.success {
  color: #92e87b;
  background: rgba(52, 184, 13, 0.12);
  border: 1px solid rgba(52, 184, 13, 0.35);
}

/* MOBILE */

@media (max-width: 768px) {
  .sell-header h1 {
    font-size: 30px;
  }

  .sell-header {
    align-items: flex-start;
  }

  .sell-tabs {
    width: 100%;
  }

  .sell-tab {
    flex: 1;
    padding: 12px 8px;
    font-size: 11px;
  }

  .sell-panel {
    padding: 16px;
  }

  .sell-panel-header h2 {
    font-size: 20px;
  }

  .sell-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .sell-item {
    min-height: 165px;
    padding: 12px;
  }

  .sell-item-name {
    font-size: 14px;
  }

  .sell-category {
    font-size: 10px;
  }

  .sell-price {
    font-size: 14px;
  }

  .sell-summary {
    flex-direction: column;
    align-items: stretch;
  }

  .sell-summary .button {
    width: 100%;
  }

  .sell-offer-actions {
    flex-direction: column;
  }

  .sell-offer-actions .button {
    width: 100%;
  }

  .sell-wallet strong {
    font-size: 25px;
  }

  .sell-withdraw-form input,
  .sell-withdraw-form .button {
    width: 100%;
  }
}
</style>
